import { useCallback } from 'react'

import type { QueryObserverResult } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { t } from 'i18next'
import { useFormContext } from 'react-hook-form'

import { ProcuradorUseCase } from '@/features/proposta/entities/Comprador/use-case/procurador-use-case'
import type { TCompradoresCadastrados } from '@/features/proposta/interfaces/proposta.type'
import type { TProcuradorPropostaSchema } from '@/features/proposta/schemas'
import type { CompradorVinculoStepSchemaInput } from '@/features/proposta/schemas/compradores/comprador-pf.schema'
import type { TCliente } from '@/shared/interfaces/cliente.types'
import { showToast } from '@/shared/lib/sonner'

type TUseDadosProcuradorProps = {
  refetch: () => Promise<QueryObserverResult<TCliente, AxiosError>>
  onNext: () => void
  compradores: TCompradoresCadastrados
  procuradores: TProcuradorPropostaSchema[]
  nacionalidadePrincipalId: string
}

export function useDadosProcuradorVinculo({
  onNext,
  compradores,
  procuradores,
  refetch,
  nacionalidadePrincipalId,
}: TUseDadosProcuradorProps) {
  const { trigger, setValue, reset, watch } = useFormContext<CompradorVinculoStepSchemaInput>()
  const compradorAtual = watch()
  const cpfProcurador = compradorAtual.dados.cpf
  const buscouCpf = compradorAtual.dados.buscouCpf
  const cpfRaw = cpfProcurador?.replace(/\D/g, '')

  const emailsJaVinculados = watch('emailsJaVinculados')

  const useCase = new ProcuradorUseCase(compradores, emailsJaVinculados, nacionalidadePrincipalId, procuradores)

  const validateOnNext = useCallback(async () => {
    const { ok, error } = useCase.validarAvanco(cpfRaw, !!buscouCpf)

    if (!ok) {
      if (error === 'MISSING_CPF') return trigger('dados.cpf')
      if (error === 'NEED_SEARCH')
        return showToast({
          variant: 'error',
          message: t('proposta:cadastro_compradores.steps.dados_comprador.erro.busca_obrigatoria'),
        })
    }
    if (await trigger('dados')) onNext()
  }, [cpfProcurador, buscouCpf, trigger, onNext])

  const handleBuscarCliente = useCallback(async () => {
    const result = await refetch()
    const fetchResult = useCase.interpretarResultadoBuscaManual(result)
    switch (fetchResult.type) {
      case 'SUCCESS': {
        const valicaoListaVinculos = useCase.validarSeCpfGeraListaVinculosVazia(fetchResult.patch.dados.id!)
        if (!valicaoListaVinculos.ok) {
          showToast({
            variant: 'error',
            message: 'Nenhum comprador elegível encontrado para vincular ao procurador.',
          })
          return
        }
        showToast({
          variant: 'success',
          message: t('proposta:cadastro_compradores.steps.dados_comprador.busca.sucesso'),
        })
        reset(fetchResult.patch)
        break
      }
      case 'NOT_FOUND':
        showToast({
          variant: 'info',
          message: t('proposta:cadastro_compradores.steps.dados_comprador.busca.nao_encontrado'),
        })
        setValue('dados.buscouCpf', true)
        break
      case 'ERROR':
        showToast({
          variant: 'error',
          message: t('proposta:cadastro_compradores.steps.dados_comprador.busca.erro'),
        })
        break
    }
  }, [cpfRaw])

  const handleLimpar = useCallback(() => {
    reset(useCase.limparProcurador())
  }, [reset])

  return {
    handlers: {
      validateOnNext,
      handleBuscarCliente,
      handleLimpar,
    },
  }
}
